import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { useSetMeetingDetailsMutation, MeetingDocument } from '../../../../graphql/react-operations';
import { type FormValues, MeetingDetailsForm } from '../../../common/components/meeting/MeetingDetailsForm';
import { type SubmitHandler } from 'react-hook-form';
import { Trans } from '@lingui/react';
import { DateTime } from 'luxon';
import { useMeetingStepDetailsSuspenseQuery, MeetingStepDetailsDocument } from './Details.graphql';
import { toast } from '@startuptools/ui/toast/use-toast';
import { captureException } from '@sentry/react';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { GqlMeeting } from '../../../../graphql/base-types.graphql';

export const MeetingStepDetails = ({
  meeting,
}: {
  meeting: Pick<
    GqlMeeting,
    'id' | 'kind' | 'usesStepper' | 'endTime' | 'startTime' | 'phone' | 'place' | 'heldKind' | 'number'
  >;
}) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();

  const {
    data: { meetings, company },
  } = useMeetingStepDetailsSuspenseQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetDetails] = useSetMeetingDetailsMutation({
    refetchQueries: [MeetingDocument, MeetingStepDetailsDocument],
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ startTime, endTime, meetingTemplateId: _, ...input }) => {
    try {
      await doSetDetails({
        variables: {
          companyId,
          input: {
            id: meeting.id,
            startTime: startTime.toString(),
            endTime: endTime?.toString() ?? null,
            ...input,
          },
        },
      });
      return stepNext();
    } catch (e) {
      toast({
        title: <Trans id="Error!" />,
        severity: 'danger',
        description: <Trans id="Could not save meeting details, please try again" />,
      });
      captureException(e);
    }
  };

  return (
    <Box>
      <MeetingDetailsForm
        onSubmit={onSubmit}
        company={company}
        meetings={meetings.models}
        meetingKind={meeting.kind}
        usesStepper={meeting.usesStepper}
        formDefaults={{
          startTime: DateTime.fromISO(meeting.startTime),
          endTime: meeting.endTime ? DateTime.fromISO(meeting.endTime) : null,
          phone: meeting.phone ?? null,
          place: meeting.place ?? null,
          heldKind: meeting.heldKind,
          number: meeting.number ?? null,
        }}
        isEditing
        actions={loading => (
          <Box>
            <Button type="submit" loading={loading}>
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        )}
      />
    </Box>
  );
};
